import { useEffect, useRef, useState } from 'react';
import { FormField } from '@/shared/ui/FormField';
import { findCityByCoords, suggestAddress, type AddressSuggest } from './api';
import type { ProfileFormApi } from './ProfileInputs';

/** Yandex address suggest: fills formatted address + coordinates (+ city when possible). */
export const AddressSuggestField = ({ form }: { form: ProfileFormApi }) => {
  const { values, errors, patch } = form;
  const [query, setQuery] = useState(values.address);
  const [options, setOptions] = useState<AddressSuggest[]>([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    setQuery(values.address);
  }, [values.address]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const loadSuggest = (value: string) => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      void suggestAddress(value).then((items) => {
        setOptions(items);
        setOpen(items.length > 0);
      });
    }, 300);
  };

  const applySuggest = async (item: AddressSuggest) => {
    const [lat, long] = item.coords;
    patch({ address: item.text, lat, long }, 'address', 'lat', 'long');
    setQuery(item.text);
    setOpen(false);
    setOptions([]);

    try {
      const city = await findCityByCoords(lat, long);
      if (city?.id) {
        patch({ cityId: String(city.id), cityName: city.name || '' }, 'city_id');
      }
    } catch {
      /* city lookup is best-effort */
    }
  };

  return (
    <FormField label="Адрес" errors={errors.address}>
      <div ref={wrapRef} style={{ position: 'relative', maxWidth: 480 }}>
        <input
          className="form-control input-sm"
          placeholder="Адрес СТО (улица, номер дома)"
          value={query}
          autoComplete="off"
          onFocus={() => options.length > 0 && setOpen(true)}
          onChange={(e) => {
            const value = e.target.value;
            setQuery(value);
            patch({ address: value }, 'address');
            loadSuggest(value);
          }}
        />
        {open && options.length > 0 && (
          <ul
            style={{
              position: 'absolute',
              zIndex: 20,
              left: 0,
              right: 0,
              margin: 0,
              padding: 0,
              listStyle: 'none',
              background: '#fff',
              border: '1px solid #d2d6de',
              borderTop: 'none',
              maxHeight: 220,
              overflowY: 'auto',
            }}
          >
            {options.map((item) => (
              <li key={`${item.text}_${item.coords.join('_')}`}>
                <button
                  type="button"
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 10px',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => void applySuggest(item)}
                >
                  {item.text}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </FormField>
  );
};
